import React from 'react';
import { Paper, Typography, Box, Chip } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { formatConnectionTime, getCategoryStyle } from '../../utils/connectionUtils';

function FlightTile({ flight, sourceColumnId, params, isDragOverlay = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: flight.uniqueId,
    data: { flight, sourceColumnId },
    disabled: isDragOverlay,
  });

  const style = {
    transform: isDragOverlay ? undefined : CSS.Translate.toString(transform),
    touchAction: 'none',
  };

  const category = flight.connectionCategory || 'normal';
  const categoryStyles = getCategoryStyle(category);
  const displayFlightNumber = flight.originalFlightNumber || `${flight.flightNumber}`;
  
  const freightCount = flight.allFreight?.reduce((total, item) => {
    return total + parseInt(item.count || 0, 10);
  }, 0) || 0;

  // --- NEW: Check for high bag count ---
  const isHighBagCount = (flight.bags || 0) >= 15;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      sx={{
        p: 1.5,
        mb: isDragOverlay ? 0 : 1,
        position: 'relative',
        cursor: isDragOverlay ? 'grabbing' : 'grab',
        backgroundColor: categoryStyles.backgroundColor,
        border: '1px solid rgba(255,255,255,0.1)',
        transition: 'transform 0.2s ease, background-color 0.2s ease, opacity 0.2s ease',
        visibility: !isDragOverlay && isDragging ? 'hidden' : 'visible',
        transform: isDragOverlay ? 'scale(1.05)' : 'none',
        boxShadow: isDragOverlay ? '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.2)' : 'none',
        '&:hover': isDragOverlay ? {} : {
          backgroundColor: category === 'normal' ? '#4a5568' : categoryStyles.backgroundColor,
          opacity: 0.9,
          transform: 'translateY(-2px)',
        }
      }}
    >
      <Typography sx={{ color: '#fff', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <span>{displayFlightNumber} &rarr; {flight.destination}</span>
        <span style={{ color: categoryStyles.color, fontWeight: 'bold' }}>
          {formatConnectionTime(flight.connectionTime)}
        </span>
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip 
          label={`${flight.bags || 0} Bags`} 
          size="small" 
          sx={{ 
            // --- UPDATED: Muted red styling for the bag chip ---
            backgroundColor: isHighBagCount ? 'rgba(255, 0, 0, 0.3)' : 'rgba(255,255,255,0.15)', 
            color: isHighBagCount ? '#ffcdd2' : '#fff', // Lighter red text for contrast
            fontWeight: isHighBagCount ? 'bold' : 'normal'
          }} 
        />
        {freightCount > 0 && (
           <Chip 
             label={flight.allFreight?.length === 1 
               ? `${flight.allFreight[0].type} ${flight.allFreight[0].pieces || flight.allFreight[0].count}/${flight.allFreight[0].weight || 0}`
               : `${freightCount} Freight`
             } 
             size="small" 
             sx={{ backgroundColor: 'rgba(249, 182, 18, 0.3)', color: '#F9B612', fontWeight: 'bold' }} 
           />
        )}
      </Box>
      
      <Typography variant="body2" sx={{ color: 'lightgrey', mt: 1 }}>
        ETD: {flight.etd} | Gate: {flight.gate}
      </Typography>
    </Paper>
  );
}

export default FlightTile;
